import { getApiBaseUrl, apiUrl } from '../lib/api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Project } from '../types';
import { getAuthToken } from '../lib/supabase';
import { Upload, FileJson, ListFilter, Loader2, Eye, Download, Filter } from 'lucide-react';

interface TasksTabProps {
  project: Project;
}

interface TaskRow {
  id: string;
  created_at: string;
  review_level: number;
  task_json: any;
}

export const TasksTab: React.FC<TasksTabProps> = ({ project }) => {
  const API_URL = getApiBaseUrl();
  const [file, setFile] = useState<File | null>(null);
  // JSONL is the only supported format
  // const [format] = useState<'jsonl'>('jsonl');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isDownloading, setIsDownloading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [jsonModal, setJsonModal] = useState<{ open: boolean; json: any }>(() => ({ open: false, json: null }));
  const [interfaceModal, setInterfaceModal] = useState<{ open: boolean; files: Record<string, string> | null; reviewLevel?: number; taskId?: string }>({ open: false, files: null });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const resetFile = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const fetchTasks = useCallback(async () => {
    try {
      setLoadingList(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      
      // Use filtered endpoint if we have a status filter, otherwise use original endpoint
      if (statusFilter !== 'all') {
        const params = new URLSearchParams();
        if (statusFilter !== 'all') params.set('status', statusFilter);
        const resp = await fetch(apiUrl(`/api/tasks/project/${project.id}/filtered?${params.toString()}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load tasks');
        setTasks(data.tasks || []);
        setTotal(data.total || 0);
      } else {
        // Use original endpoint for all tasks
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        const resp = await fetch(apiUrl(`/api/projects/${project.id}/tasks?${params.toString()}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load tasks');
        setTasks(data.tasks || []);
        setTotal(data.total || 0);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load tasks');
    } finally {
      setLoadingList(false);
    }
  }, [API_URL, project.id, page, limit, statusFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Only JSONL supported, so we don't infer other formats
  // Removed format inference since only JSONL is supported

  const createFileFromText = (raw: string): File => {
    const cleaned = raw.replace(/```[a-zA-Z]*\n?|```/g, '');
    const blob = new Blob([cleaned], { type: 'application/x-ndjson;charset=utf-8' });
    return new File([blob], 'pasted.jsonl', { type: 'application/x-ndjson' });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const dropped = dt.files[0];
      const name = dropped.name.toLowerCase();
      const type = dropped.type.toLowerCase();
      const isJsonl = name.endsWith('.jsonl') || name.endsWith('.ndjson') || type.includes('ndjson') || type.includes('jsonl');
      if (!isJsonl) {
        setError('Only JSONL files are supported.');
        return;
      }
      setFile(dropped);
      return;
    }
    const text = dt.getData('text/plain');
    if (text && text.trim().length > 0) {
      const f = createFileFromText(text);
      setFile(f);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    try {
      setError(null);
      const files = e.clipboardData?.files;
      if (files && files.length > 0) {
        const pasted = files[0];
        const name = pasted.name?.toLowerCase?.() || '';
        const type = pasted.type?.toLowerCase?.() || '';
        const isJsonl = name.endsWith('.jsonl') || name.endsWith('.ndjson') || type.includes('ndjson') || type.includes('jsonl');
        if (!isJsonl) {
          setError('Only JSONL files are supported.');
          return;
        }
        setFile(pasted);
        return;
      }
      const text = e.clipboardData?.getData('text/plain');
      if (text && text.trim().length > 0) {
        const f = createFileFromText(text);
        setFile(f);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to read pasted content');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      const form = new FormData();
      form.append('file', file);
      form.append('format', 'jsonl');
      const resp = await fetch(apiUrl(`/api/projects/${project.id}/tasks/upload`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Upload failed');
      resetFile();
      await fetchTasks();
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      
      const params = new URLSearchParams({ format: 'jsonl' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      
      const resp = await fetch(apiUrl(`/api/tasks/project/${project.id}/filtered?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Download failed');
      }
      
      // Create download link
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks_${project.id}_${statusFilter}_${new Date().toISOString().split('T')[0]}.jsonl`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e: any) {
      setError(e?.message || 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-4">
        <h3 className="text-lg font-semibold text-zinc-900 mb-3">Upload Tasks</h3>
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-zinc-700 mb-1">Drag & Drop or Paste</label>
            <div
              tabIndex={0}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onPaste={handlePaste}
              className={`w-full rounded-xl border-2 ${isDragging ? 'border-zinc-500 bg-zinc-50' : 'border-dashed border-zinc-300'} p-4 text-sm text-zinc-600 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p>Drag and drop a JSONL file here, or click then press Cmd/Ctrl+V to paste JSONL lines.</p>
                  {file && (
                    <p className="mt-2 text-zinc-800"><strong>Selected:</strong> {file.name} ({Math.ceil(file.size / 1024)} KB)</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { if (inputRef.current) inputRef.current.click(); }}
                  className="ml-4 px-3 py-1 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:ring-zinc-400"
                >Browse…</button>
              </div>
            </div>
            {/* Hidden file input triggered by Browse button */}
            <input
              ref={inputRef}
              type="file"
              accept=".jsonl,.ndjson,application/x-ndjson,application/jsonl"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </div>
          {/* Format selector removed; JSONL only */}
          <div>
            <button
              onClick={handleUpload}
              disabled={!file || isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-full disabled:bg-zinc-300"
              style={{ backgroundColor: 'var(--accent)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </button>
          </div>
        </div>
        
        {error && (
          <div className="mt-3 text-sm text-red-600">{error}</div>
        )}
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-zinc-200">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-zinc-900">Tasks</h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-500" />
              <select
                value={statusFilter}
                onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                className="rounded-xl border-zinc-300 focus:border-transparent focus:ring-2 focus:ring-zinc-900 text-sm"
              >
                <option value="all">All Statuses</option>
                <option value="not-started">Not Started</option>
                <option value="attempted">Attempted</option>
                <option value="reviewed">Reviewed</option>
                <option value="sent-back">Sent Back</option>
              </select>
            </div>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-xl bg-white ring-1 ring-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:ring-zinc-400 disabled:opacity-50"
              title="Download filtered tasks as JSONL"
            >
              {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download JSONL
            </button>
            <div className="flex items-center gap-2">
              <ListFilter className="w-4 h-4 text-zinc-500" />
              <select
                value={limit}
                onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }}
                className="rounded-xl border-zinc-300 focus:border-transparent focus:ring-2 focus:ring-zinc-900 text-sm"
              >
                {[25,50,100,200].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="p-0 overflow-x-auto">
          {loadingList ? (
            <div className="py-10 flex items-center justify-center text-zinc-600">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading tasks...
            </div>
          ) : tasks.length === 0 ? (
            <div className="py-10 text-center text-zinc-500">No tasks yet.</div>
          ) : (
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600 uppercase tracking-wider">Preview</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {tasks.map((t) => (
                  <tr key={t.id}>
                    <td className="px-4 py-2 text-sm text-zinc-900">{t.id}</td>
                    <td className="px-4 py-2 text-sm text-zinc-600">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm">
                      {t.review_level === -1 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200">Not Started</span>
                      )}
                      {t.review_level === 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 ring-1 ring-amber-200">Attempted</span>
                      )}
                      {t.review_level === 1 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200">Reviewed</span>
                      )}
                      {t.review_level !== -1 && t.review_level !== 0 && t.review_level !== 1 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200">Reviewed</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2 text-zinc-700 text-xs">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const token = await getAuthToken();
                              if (!token) throw new Error('Not authenticated');
                              const resp = await fetch(apiUrl(`/api/tasks/get`), {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ previewMode: true, taskId: t.id })
                              });
                              const data = await resp.json();
                              if (!resp.ok) throw new Error(data.error || 'Failed to fetch task JSON');
                              setJsonModal({ open: true, json: data.taskJson });
                            } catch (err: any) {
                              setError(err?.message || 'Failed to fetch task JSON');
                            }
                          }}
                          className="px-2.5 py-1 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:ring-zinc-400 inline-flex items-center gap-1"
                          title="View JSON"
                        >
                          <FileJson className="w-4 h-4" /> View JSON
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const token = await getAuthToken();
                              if (!token) throw new Error('Not authenticated');
                              // Get latest interface files for the project
                              const codeResp = await fetch(apiUrl(`/api/projects/${project.id}/code`), {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              const codeData = await codeResp.json();
                              if (!codeResp.ok) throw new Error(codeData.error || 'Failed to load interface code');
                              setInterfaceModal({ open: true, files: codeData.files || {}, reviewLevel: t.review_level, taskId: t.id });
                            } catch (err: any) {
                              setError(err?.message || 'Failed to open interface preview');
                            }
                          }}
                          className="px-2.5 py-1 rounded-full text-white inline-flex items-center gap-1"
                          style={{ backgroundColor: 'var(--accent)' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
                          title="View task on interface"
                        >
                          <Eye className="w-4 h-4" /> View on Interface
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {tasks.length > 0 && (
          <div className="px-4 py-3 border-t border-zinc-200 flex items-center justify-between">
            <div className="text-sm text-zinc-600">
              Page {page} of {totalPages} • {total} tasks
              {statusFilter !== 'all' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Filter: {statusFilter.replace('-', ' ')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-full bg-white ring-1 ring-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:ring-zinc-400 disabled:bg-white disabled:text-zinc-400"
              >Prev</button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm rounded-full bg-white ring-1 ring-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:ring-zinc-400 disabled:bg-white disabled:text-zinc-400"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {jsonModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setJsonModal({ open: false, json: null })} />
          <div className="relative bg-white w-[min(90vw,700px)] max-h-[80vh] rounded-2xl shadow-xl ring-1 ring-zinc-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-zinc-900">Task JSON</h4>
              <button
                onClick={() => setJsonModal({ open: false, json: null })}
                className="px-2 py-1 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-50"
              >Close</button>
            </div>
            <div className="p-4 overflow-auto">
              <pre className="text-[11px] bg-zinc-50 border border-zinc-200 rounded p-3 whitespace-pre-wrap break-all">{JSON.stringify(jsonModal.json, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}

      {interfaceModal.open && interfaceModal.files && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setInterfaceModal({ open: false, files: null })} />
          <div className="relative bg-white w-[min(95vw,1100px)] h-[min(90vh,800px)] rounded-2xl shadow-xl ring-1 ring-zinc-200 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-zinc-900">Interface Preview</h4>
              <button
                onClick={() => setInterfaceModal({ open: false, files: null })}
                className="px-2 py-1 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-50"
              >Close</button>
            </div>
            <div className="flex-1">
              {/* Inline iframe preview */}
              <iframe
                title="Interface Preview"
                style={{ width: '100%', height: '100%', border: 0 }}
                srcDoc={(interfaceModal.files['index.html'] || '<!doctype html><html><head></head><body><pre>No interface</pre></body></html>')
                  .replace('%SUPABASE_URL%', (import.meta as any).env.VITE_SUPABASE_URL || '')
                  .replace('%SUPABASE_ANON_KEY%', (import.meta as any).env.VITE_SUPABASE_ANON_KEY || '')
                  .replace('</head>', `<script>window.__CONFIG__=${JSON.stringify({
                    VITE_SUPABASE_URL: (import.meta as any).env.VITE_SUPABASE_URL || '',
                    VITE_SUPABASE_ANON_KEY: (import.meta as any).env.VITE_SUPABASE_ANON_KEY || '',
                    PREVIEW_MODE: true,
                    PROJECT_ID: project.id,
                    API_URL: (import.meta as any).env.VITE_API_URL || window.location.origin,
                    PREVIEW_REVIEW_LEVEL: interfaceModal.reviewLevel,
                    PREVIEW_TASK_ID: interfaceModal.taskId
                  })};<\/script><script>(function(){\n  try {\n    var cfg = window.__CONFIG__ || {};\n    var origFetch = window.fetch;\n    window.fetch = function(input, init){\n      try {\n        var url = typeof input === 'string' ? input : (input && input.url);\n        var method = (init && init.method) ? String(init.method).toUpperCase() : 'GET';\n        // Ensure preview mode and taskId are sent to get-task\n        if (url && url.indexOf('/api/tasks/get') !== -1 && method === 'POST'){\n          var body = {};\n          try { body = init && init.body ? JSON.parse(init.body) : {}; } catch(e){}\n          if (cfg.PREVIEW_MODE) body.previewMode = true;\n          if (cfg.PREVIEW_TASK_ID) body.taskId = cfg.PREVIEW_TASK_ID;\n          if (typeof cfg.PREVIEW_REVIEW_LEVEL === 'number' && body.reviewLevel === undefined) body.reviewLevel = cfg.PREVIEW_REVIEW_LEVEL;\n          init = Object.assign({}, init, { body: JSON.stringify(body) });\n        }\n        // Force previewMode for submit/send-back so backend no-ops\n        if (url && (url.indexOf('/api/tasks/submit') !== -1 || url.indexOf('/api/tasks/send-back') !== -1) && (method === 'POST' || method === 'PUT')){\n          var body2 = {};\n          try { body2 = init && init.body ? JSON.parse(init.body) : {}; } catch(e){}\n          if (cfg.PREVIEW_MODE) body2.previewMode = true;\n          init = Object.assign({}, init, { body: JSON.stringify(body2) });\n        }\n      } catch(e) {}\n      return origFetch(input, init);\n    };\n  } catch(e){}\n})();<\/script></head>`)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


